import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  LmsAnalysisDownloadType,
  LmsJobCountsUsage,
  LmsRecordingDurationUsage,
  LmsTranscodingDurationRange,
  LmsTranscodingDurationUsage,
} from './live-analysis-types';

// ============================================
// Query keys
// ============================================

export const liveAnalysisKeys = {
  recordingDuration: (serviceId: string, year: number) =>
    ['livestream', 'analysis', 'recording-duration', serviceId, { year }] as const,
  transcodingDuration: (serviceId: string, range: LmsTranscodingDurationRange) =>
    ['livestream', 'analysis', 'transcoding-duration', serviceId, { range }] as const,
  jobCounts: (serviceId: string, year: number) =>
    ['livestream', 'analysis', 'job-counts', serviceId, { year }] as const,
};

// ============================================
// Queries
// ============================================

export function useLmsRecordingDurationUsage(
  serviceId: string,
  year: number,
  options?: Omit<UseQueryOptions<LmsRecordingDurationUsage[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LmsRecordingDurationUsage[]>({
    queryKey: liveAnalysisKeys.recordingDuration(serviceId, year),
    enabled: !!serviceId && !!year,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${mediaBase(serviceId, 'streams')}/analysis/recording-duration`,
        { params: { year: String(year) } },
      );
      return res.data?.data ?? res.data ?? [];
    },
  });
}

export function useLmsTranscodingDurationUsage(
  serviceId: string,
  range: LmsTranscodingDurationRange,
  options?: Omit<UseQueryOptions<LmsTranscodingDurationUsage[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LmsTranscodingDurationUsage[]>({
    queryKey: liveAnalysisKeys.transcodingDuration(serviceId, range),
    enabled: !!serviceId && !!range,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${mediaBase(serviceId, 'streams')}/analysis/transcoding-duration`,
        { params: { range } },
      );
      return res.data?.data ?? res.data ?? [];
    },
  });
}

export function useLmsJobCountsUsage(
  serviceId: string,
  year: number,
  options?: Omit<UseQueryOptions<LmsJobCountsUsage[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LmsJobCountsUsage[]>({
    queryKey: liveAnalysisKeys.jobCounts(serviceId, year),
    enabled: !!serviceId && !!year,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${mediaBase(serviceId, 'streams')}/analysis/job-counts`,
        { params: { year: String(year) } },
      );
      return res.data?.data ?? res.data ?? [];
    },
  });
}

// ============================================
// CSV download (imperative — not a hook)
// ============================================

/**
 * Download analysis data as CSV blob.
 * For recording-duration and job-counts: pass a numeric year.
 * For transcoding-duration: the BE CSV endpoint only supports year-based export
 * (it ignores a `range` param), so pass a numeric year here too.
 * The caller is responsible for passing the correct year value.
 */
export async function fetchLmsAnalysisCsv(
  serviceId: string,
  type: LmsAnalysisDownloadType,
  year: number,
): Promise<Blob> {
  const res = await serviceApiV3.get(
    `${mediaBase(serviceId, 'streams')}/analysis/${type}/download`,
    { params: { year: String(year) }, responseType: 'blob' },
  );
  return res.data;
}
